/**
 * Runtime Variables File Renderer (Engine-Independent)
 * Generates a runtime-modifiable variable system with dictionary-based storage
 * Supports: get/set operations, change events, persistence, validation, and reset
 * 
 * Uses Grimoire.Data.Runtime abstractions for logging, JSON, and storage
 * Works with Unity, Godot, or standalone .NET
 */

/**
 * Render runtime variables file for C# exports (engine-independent)
 * Generates a VariableRuntime static class with dictionary-based storage for runtime modification
 * 
 * @param variables - Array of variable definitions
 * @param namespace - C# namespace (default: 'Grimoire.Data')
 * @param className - Class name for the runtime variables class (default: 'VariableRuntime')
 */
export function renderRuntimeVariablesFile(
  variables: any[],
  namespace: string = 'Grimoire.Data',
  className: string = 'VariableRuntime'
): string {
  console.log(`📊 Rendering runtime variables file for namespace: ${namespace}`);
  console.log(`🔢 Processing ${variables.length} variables for runtime system`);
  
  let output = '// Auto-generated Runtime Variables\n';
  output += '// This file is AUTO-GENERATED and can be safely regenerated.\n';
  output += '// DO NOT EDIT THIS FILE DIRECTLY - your changes will be lost.\n\n';
  output += 'using System;\n';
  output += 'using System.Collections.Generic;\n';
  output += 'using System.IO;\n';
  output += 'using Grimoire.Data.Runtime;\n\n';
  output += `namespace ${namespace}\n{\n`;
  
  // Generate RangeValue struct for storing range data
  output += '\t/// <summary>Struct for storing range variable values</summary>\n';
  output += '\t[Serializable]\n';
  output += '\tpublic struct RangeValue\n\t{\n';
  output += '\t\tpublic double Min;\n';
  output += '\t\tpublic double Max;\n';
  output += '\t\tpublic double Increment;\n\n';
  output += '\t\tpublic RangeValue(double min, double max, double increment)\n\t\t{\n';
  output += '\t\t\tMin = min;\n';
  output += '\t\t\tMax = max;\n';
  output += '\t\t\tIncrement = increment;\n';
  output += '\t\t}\n';
  output += '\t}\n\n';
  
  // Generate VariableData class for JSON serialization
  output += '\t/// <summary>Data container for variable serialization</summary>\n';
  output += '\t[Serializable]\n';
  output += '\tpublic class VariableData\n\t{\n';
  output += '\t\tpublic Dictionary<string, string> StringVariables = new Dictionary<string, string>();\n';
  output += '\t\tpublic Dictionary<string, double> NumberVariables = new Dictionary<string, double>();\n';
  output += '\t\tpublic Dictionary<string, double> PercentageVariables = new Dictionary<string, double>();\n';
  output += '\t\tpublic Dictionary<string, bool> BooleanVariables = new Dictionary<string, bool>();\n';
  output += '\t\tpublic Dictionary<string, RangeValue> RangeVariables = new Dictionary<string, RangeValue>();\n';
  output += '\t}\n\n';
  
  // Generate main VariableRuntime static class
  output += `\t/// <summary>Runtime variable system with dictionary-based storage</summary>\n`;
  output += `\tpublic static class ${className}\n\t{\n`;
  
  // Storage dictionaries
  output += '\t\t#region Storage\n\n';
  output += '\t\t/// <summary>Storage for string variables</summary>\n';
  output += '\t\tprivate static Dictionary<string, string> _stringVariables = new Dictionary<string, string>();\n\n';
  output += '\t\t/// <summary>Storage for number variables</summary>\n';
  output += '\t\tprivate static Dictionary<string, double> _numberVariables = new Dictionary<string, double>();\n\n';
  output += '\t\t/// <summary>Storage for percentage variables</summary>\n';
  output += '\t\tprivate static Dictionary<string, double> _percentageVariables = new Dictionary<string, double>();\n\n';
  output += '\t\t/// <summary>Storage for boolean variables</summary>\n';
  output += '\t\tprivate static Dictionary<string, bool> _booleanVariables = new Dictionary<string, bool>();\n\n';
  output += '\t\t/// <summary>Storage for range variables</summary>\n';
  output += '\t\tprivate static Dictionary<string, RangeValue> _rangeVariables = new Dictionary<string, RangeValue>();\n\n';
  output += '\t\t/// <summary>Tracks if the system has been initialized</summary>\n';
  output += '\t\tprivate static bool _isInitialized = false;\n\n';
  output += '\t\t#endregion\n\n';
  
  // Event system
  output += '\t\t#region Events\n\n';
  output += '\t\t/// <summary>Delegate for variable change notifications</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="oldValue">Previous value</param>\n';
  output += '\t\t/// <param name="newValue">New value</param>\n';
  output += '\t\tpublic delegate void VariableChangedHandler(string group, string name, object oldValue, object newValue);\n\n';
  output += '\t\t/// <summary>Event fired when any variable changes</summary>\n';
  output += '\t\tpublic static event VariableChangedHandler OnVariableChanged;\n\n';
  output += '\t\t#endregion\n\n';
  
  // Helper methods
  output += '\t\t#region Helper Methods\n\n';
  output += '\t\t/// <summary>Creates a dictionary key from group and name</summary>\n';
  output += '\t\tprivate static string MakeKey(string group, string name)\n\t\t{\n';
  output += '\t\t\treturn $"{group}:{name}";\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Clamps a value between min and max</summary>\n';
  output += '\t\tprivate static double Clamp(double value, double min, double max)\n\t\t{\n';
  output += '\t\t\tif (value < min) return min;\n';
  output += '\t\t\tif (value > max) return max;\n';
  output += '\t\t\treturn value;\n';
  output += '\t\t}\n\n';
  output += '\t\t#endregion\n\n';
  
  // Getter methods
  output += '\t\t#region Getters\n\n';
  output += '\t\t/// <summary>Gets a string variable value</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="defaultValue">Default value if not found</param>\n';
  output += '\t\t/// <returns>The string value</returns>\n';
  output += '\t\tpublic static string GetString(string group, string name, string defaultValue = "")\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tif (_stringVariables.TryGetValue(key, out string value))\n\t\t\t{\n';
  output += '\t\t\t\treturn value;\n';
  output += '\t\t\t}\n';
  output += '\t\t\tGrimoireLog.Warn($"String variable not found: {group}.{name}");\n';
  output += '\t\t\treturn defaultValue;\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Gets a number variable value</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="defaultValue">Default value if not found</param>\n';
  output += '\t\t/// <returns>The numeric value</returns>\n';
  output += '\t\tpublic static double GetNumber(string group, string name, double defaultValue = 0.0)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tif (_numberVariables.TryGetValue(key, out double value))\n\t\t\t{\n';
  output += '\t\t\t\treturn value;\n';
  output += '\t\t\t}\n';
  output += '\t\t\tGrimoireLog.Warn($"Number variable not found: {group}.{name}");\n';
  output += '\t\t\treturn defaultValue;\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Gets a percentage variable value (0-100)</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="defaultValue">Default value if not found</param>\n';
  output += '\t\t/// <returns>The percentage value (0-100)</returns>\n';
  output += '\t\tpublic static double GetPercentage(string group, string name, double defaultValue = 0.0)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tif (_percentageVariables.TryGetValue(key, out double value))\n\t\t\t{\n';
  output += '\t\t\t\treturn value;\n';
  output += '\t\t\t}\n';
  output += '\t\t\tGrimoireLog.Warn($"Percentage variable not found: {group}.{name}");\n';
  output += '\t\t\treturn defaultValue;\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Gets a percentage variable as a normalized value (0.0-1.0)</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="defaultValue">Default value if not found</param>\n';
  output += '\t\t/// <returns>The normalized value (0.0-1.0)</returns>\n';
  output += '\t\tpublic static double GetPercentageNormalized(string group, string name, double defaultValue = 0.0)\n\t\t{\n';
  output += '\t\t\treturn GetPercentage(group, name, defaultValue) / 100.0;\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Gets a range variable value</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <returns>The range value struct</returns>\n';
  output += '\t\tpublic static RangeValue GetRange(string group, string name)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tif (_rangeVariables.TryGetValue(key, out RangeValue value))\n\t\t\t{\n';
  output += '\t\t\t\treturn value;\n';
  output += '\t\t\t}\n';
  output += '\t\t\tGrimoireLog.Warn($"Range variable not found: {group}.{name}");\n';
  output += '\t\t\treturn new RangeValue(0, 0, 1);\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Gets a boolean variable value</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="defaultValue">Default value if not found</param>\n';
  output += '\t\t/// <returns>The boolean value</returns>\n';
  output += '\t\tpublic static bool GetBoolean(string group, string name, bool defaultValue = false)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tif (_booleanVariables.TryGetValue(key, out bool value))\n\t\t\t{\n';
  output += '\t\t\t\treturn value;\n';
  output += '\t\t\t}\n';
  output += '\t\t\tGrimoireLog.Warn($"Boolean variable not found: {group}.{name}");\n';
  output += '\t\t\treturn defaultValue;\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Checks if a variable exists</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <returns>True if the variable exists</returns>\n';
  output += '\t\tpublic static bool HasVariable(string group, string name)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\treturn _stringVariables.ContainsKey(key) ||\n';
  output += '\t\t\t       _numberVariables.ContainsKey(key) ||\n';
  output += '\t\t\t       _percentageVariables.ContainsKey(key) ||\n';
  output += '\t\t\t       _booleanVariables.ContainsKey(key) ||\n';
  output += '\t\t\t       _rangeVariables.ContainsKey(key);\n';
  output += '\t\t}\n\n';
  output += '\t\t#endregion\n\n';
  
  // Setter methods
  output += '\t\t#region Setters\n\n';
  output += '\t\t/// <summary>Sets a string variable value</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="value">New value</param>\n';
  output += '\t\tpublic static void SetString(string group, string name, string value)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tstring oldValue = _stringVariables.ContainsKey(key) ? _stringVariables[key] : "";\n';
  output += '\t\t\t_stringVariables[key] = value;\n';
  output += '\t\t\tOnVariableChanged?.Invoke(group, name, oldValue, value);\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Sets a number variable value</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="value">New value</param>\n';
  output += '\t\tpublic static void SetNumber(string group, string name, double value)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tdouble oldValue = _numberVariables.ContainsKey(key) ? _numberVariables[key] : 0.0;\n';
  output += '\t\t\t_numberVariables[key] = value;\n';
  output += '\t\t\tOnVariableChanged?.Invoke(group, name, oldValue, value);\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Sets a percentage variable value with clamping (0-100)</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="value">New value (will be clamped to 0-100)</param>\n';
  output += '\t\tpublic static void SetPercentage(string group, string name, double value)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tdouble oldValue = _percentageVariables.ContainsKey(key) ? _percentageVariables[key] : 0.0;\n';
  output += '\t\t\tdouble clampedValue = Clamp(value, 0.0, 100.0);\n';
  output += '\t\t\t_percentageVariables[key] = clampedValue;\n';
  output += '\t\t\tOnVariableChanged?.Invoke(group, name, oldValue, clampedValue);\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Sets a boolean variable value</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="value">New value</param>\n';
  output += '\t\tpublic static void SetBoolean(string group, string name, bool value)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tbool oldValue = _booleanVariables.ContainsKey(key) ? _booleanVariables[key] : false;\n';
  output += '\t\t\t_booleanVariables[key] = value;\n';
  output += '\t\t\tOnVariableChanged?.Invoke(group, name, oldValue, value);\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Sets a range variable value with validation</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\t/// <param name="min">Minimum value</param>\n';
  output += '\t\t/// <param name="max">Maximum value</param>\n';
  output += '\t\t/// <param name="increment">Increment/step value</param>\n';
  output += '\t\tpublic static void SetRange(string group, string name, double min, double max, double increment)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tRangeValue oldValue = _rangeVariables.ContainsKey(key) ? _rangeVariables[key] : new RangeValue(0, 0, 1);\n';
  output += '\t\t\t\n';
  output += '\t\t\t// Validate that min <= max\n';
  output += '\t\t\tif (min > max)\n\t\t\t{\n';
  output += '\t\t\t\tGrimoireLog.Warn($"Range variable {group}.{name}: min ({min}) > max ({max}). Swapping values.");\n';
  output += '\t\t\t\tdouble temp = min;\n';
  output += '\t\t\t\tmin = max;\n';
  output += '\t\t\t\tmax = temp;\n';
  output += '\t\t\t}\n\n';
  output += '\t\t\t// Ensure increment is positive\n';
  output += '\t\t\tif (increment <= 0)\n\t\t\t{\n';
  output += '\t\t\t\tGrimoireLog.Warn($"Range variable {group}.{name}: increment ({increment}) <= 0. Setting to 1.");\n';
  output += '\t\t\t\tincrement = 1.0;\n';
  output += '\t\t\t}\n\n';
  output += '\t\t\tRangeValue newValue = new RangeValue(min, max, increment);\n';
  output += '\t\t\t_rangeVariables[key] = newValue;\n';
  output += '\t\t\tOnVariableChanged?.Invoke(group, name, oldValue, newValue);\n';
  output += '\t\t}\n\n';
  output += '\t\t#endregion\n\n';
  
  // Persistence methods
  output += '\t\t#region Persistence\n\n';
  output += '\t\t/// <summary>Saves all variables to a JSON file</summary>\n';
  output += '\t\t/// <param name="filePath">Path to save the JSON file</param>\n';
  output += '\t\tpublic static void SaveToJson(string filePath)\n\t\t{\n';
  output += '\t\t\ttry\n\t\t\t{\n';
  output += '\t\t\t\tVariableData data = new VariableData\n\t\t\t\t{\n';
  output += '\t\t\t\t\tStringVariables = _stringVariables,\n';
  output += '\t\t\t\t\tNumberVariables = _numberVariables,\n';
  output += '\t\t\t\t\tPercentageVariables = _percentageVariables,\n';
  output += '\t\t\t\t\tRangeVariables = _rangeVariables\n';
  output += '\t\t\t\t};\n\n';
  output += '\t\t\t\tstring json = GrimoireJson.ToJson(data, true);\n';
  output += '\t\t\t\tFile.WriteAllText(filePath, json);\n';
  output += '\t\t\t\tGrimoireLog.Info($"Variables saved to: {filePath}");\n';
  output += '\t\t\t}\n';
  output += '\t\t\tcatch (Exception ex)\n\t\t\t{\n';
  output += '\t\t\t\tGrimoireLog.Error($"Failed to save variables to {filePath}: {ex.Message}");\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Loads all variables from a JSON file</summary>\n';
  output += '\t\t/// <param name="filePath">Path to the JSON file</param>\n';
  output += '\t\t/// <returns>True if loaded successfully</returns>\n';
  output += '\t\tpublic static bool LoadFromJson(string filePath)\n\t\t{\n';
  output += '\t\t\ttry\n\t\t\t{\n';
  output += '\t\t\t\tif (!File.Exists(filePath))\n\t\t\t\t{\n';
  output += '\t\t\t\t\tGrimoireLog.Warn($"Variable file not found: {filePath}");\n';
  output += '\t\t\t\t\treturn false;\n';
  output += '\t\t\t\t}\n\n';
  output += '\t\t\t\tstring json = File.ReadAllText(filePath);\n';
  output += '\t\t\t\tVariableData data = GrimoireJson.FromJson<VariableData>(json);\n\n';
  output += '\t\t\t\tif (data != null)\n\t\t\t\t{\n';
  output += '\t\t\t\t\t_stringVariables = data.StringVariables ?? new Dictionary<string, string>();\n';
  output += '\t\t\t\t\t_numberVariables = data.NumberVariables ?? new Dictionary<string, double>();\n';
  output += '\t\t\t\t\t_percentageVariables = data.PercentageVariables ?? new Dictionary<string, double>();\n';
  output += '\t\t\t\t\t_booleanVariables = data.BooleanVariables ?? new Dictionary<string, bool>();\n';
  output += '\t\t\t\t\t_rangeVariables = data.RangeVariables ?? new Dictionary<string, RangeValue>();\n';
  output += '\t\t\t\t\tGrimoireLog.Info($"Variables loaded from: {filePath}");\n';
  output += '\t\t\t\t\treturn true;\n';
  output += '\t\t\t\t}\n';
  output += '\t\t\t\treturn false;\n';
  output += '\t\t\t}\n';
  output += '\t\t\tcatch (Exception ex)\n\t\t\t{\n';
  output += '\t\t\t\tGrimoireLog.Error($"Failed to load variables from {filePath}: {ex.Message}");\n';
  output += '\t\t\t\treturn false;\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Saves all variables to PlayerPrefs</summary>\n';
  output += '\t\t/// <param name="key">PlayerPrefs key (default: "VariableRuntime")</param>\n';
  output += '\t\tpublic static void SaveToPlayerPrefs(string key = "VariableRuntime")\n\t\t{\n';
  output += '\t\t\ttry\n\t\t\t{\n';
  output += '\t\t\t\tVariableData data = new VariableData\n\t\t\t\t{\n';
  output += '\t\t\t\t\tStringVariables = _stringVariables,\n';
  output += '\t\t\t\t\tNumberVariables = _numberVariables,\n';
  output += '\t\t\t\t\tPercentageVariables = _percentageVariables,\n';
  output += '\t\t\t\t\tBooleanVariables = _booleanVariables,\n';
  output += '\t\t\t\t\tRangeVariables = _rangeVariables\n';
  output += '\t\t\t\t};\n\n';
  output += '\t\t\t\tstring json = GrimoireJson.ToJson(data);\n';
  output += '\t\t\t\tGrimoireStorage.SetString(key, json);\n';
  output += '\t\t\t\tGrimoireStorage.Save();\n';
  output += '\t\t\t\tGrimoireLog.Info($"Variables saved to PlayerPrefs with key: {key}");\n';
  output += '\t\t\t}\n';
  output += '\t\t\tcatch (Exception ex)\n\t\t\t{\n';
  output += '\t\t\t\tGrimoireLog.Error($"Failed to save variables to PlayerPrefs: {ex.Message}");\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Loads all variables from PlayerPrefs</summary>\n';
  output += '\t\t/// <param name="key">PlayerPrefs key (default: "VariableRuntime")</param>\n';
  output += '\t\t/// <returns>True if loaded successfully</returns>\n';
  output += '\t\tpublic static bool LoadFromPlayerPrefs(string key = "VariableRuntime")\n\t\t{\n';
  output += '\t\t\ttry\n\t\t\t{\n';
  output += '\t\t\t\tif (!GrimoireStorage.HasKey(key))\n\t\t\t\t{\n';
  output += '\t\t\t\t\tGrimoireLog.Warn($"PlayerPrefs key not found: {key}");\n';
  output += '\t\t\t\t\treturn false;\n';
  output += '\t\t\t\t}\n\n';
  output += '\t\t\t\tstring json = GrimoireStorage.GetString(key);\n';
  output += '\t\t\t\tVariableData data = GrimoireJson.FromJson<VariableData>(json);\n\n';
  output += '\t\t\t\tif (data != null)\n\t\t\t\t{\n';
  output += '\t\t\t\t\t_stringVariables = data.StringVariables ?? new Dictionary<string, string>();\n';
  output += '\t\t\t\t\t_numberVariables = data.NumberVariables ?? new Dictionary<string, double>();\n';
  output += '\t\t\t\t\t_percentageVariables = data.PercentageVariables ?? new Dictionary<string, double>();\n';
  output += '\t\t\t\t\t_booleanVariables = data.BooleanVariables ?? new Dictionary<string, bool>();\n';
  output += '\t\t\t\t\t_rangeVariables = data.RangeVariables ?? new Dictionary<string, RangeValue>();\n';
  output += '\t\t\t\t\tGrimoireLog.Info($"Variables loaded from PlayerPrefs with key: {key}");\n';
  output += '\t\t\t\t\treturn true;\n';
  output += '\t\t\t\t}\n';
  output += '\t\t\t\treturn false;\n';
  output += '\t\t\t}\n';
  output += '\t\t\tcatch (Exception ex)\n\t\t\t{\n';
  output += '\t\t\t\tGrimoireLog.Error($"Failed to load variables from PlayerPrefs: {ex.Message}");\n';
  output += '\t\t\t\treturn false;\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n\n';
  output += '\t\t#endregion\n\n';
  
  // Initialize and Reset methods
  output += '\t\t#region Initialize and Reset\n\n';
  output += '\t\t/// <summary>Initializes the runtime variable system with default values from Variables class</summary>\n';
  output += '\t\tpublic static void Initialize()\n\t\t{\n';
  output += '\t\t\tif (_isInitialized)\n\t\t\t{\n';
  output += '\t\t\t\tGrimoireLog.Warn("VariableRuntime already initialized. Call Reset() first if you want to reinitialize.");\n';
  output += '\t\t\t\treturn;\n';
  output += '\t\t\t}\n\n';
  output += '\t\t\tGrimoireLog.Info("Initializing VariableRuntime with default values...");\n\n';
  
  // Helper function to sanitize C# identifiers
  const sanitizeName = (name: string): string => {
    return name.replace(/[^a-zA-Z0-9_]/g, '_');
  };
  
  // Generate initialization code for each variable
  variables.forEach((variable, varIndex) => {
    if (!variable.data || !Array.isArray(variable.data) || variable.data.length === 0) {
      return;
    }
    
    const variableName = sanitizeName(variable.name || `Variable${varIndex}`);
    const elementType = variable.element_type || 'string';
    
    output += `\t\t\t// Initialize ${variableName} (${elementType})\n`;
    
    variable.data.forEach((element: any) => {
      const elementName = sanitizeName(element.name || element.id || 'Unknown');
      const key = `${variableName}:${elementName}`;
      
      if (elementType === 'string') {
        const value = (element.name || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        output += `\t\t\t_stringVariables["${key}"] = Variables.${variableName}.${elementName}.Value;\n`;
      } else if (elementType === 'number') {
        output += `\t\t\t_numberVariables["${key}"] = Variables.${variableName}.${elementName}.GetValue();\n`;
      } else if (elementType === 'percentage') {
        output += `\t\t\t_percentageVariables["${key}"] = Variables.${variableName}.${elementName}.GetValue();\n`;
      } else if (elementType === 'boolean') {
        output += `\t\t\t_booleanVariables["${key}"] = Variables.${variableName}.${elementName}.GetValue();\n`;
      } else if (elementType === 'range') {
        output += `\t\t\t_rangeVariables["${key}"] = new RangeValue(\n`;
        output += `\t\t\t\tVariables.${variableName}.${elementName}.GetMin(),\n`;
        output += `\t\t\t\tVariables.${variableName}.${elementName}.GetMax(),\n`;
        output += `\t\t\t\tVariables.${variableName}.${elementName}.GetIncrement()\n`;
        output += `\t\t\t);\n`;
      }
    });
    output += '\n';
  });
  
  output += '\t\t\t_isInitialized = true;\n';
  output += '\t\t\tGrimoireLog.Info($"VariableRuntime initialized with {_stringVariables.Count} strings, {_numberVariables.Count} numbers, {_percentageVariables.Count} percentages, {_booleanVariables.Count} booleans, {_rangeVariables.Count} ranges");\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Resets all variables to their default values from the Variables class</summary>\n';
  output += '\t\tpublic static void Reset()\n\t\t{\n';
  output += '\t\t\tGrimoireLog.Info("Resetting VariableRuntime to default values...");\n';
  output += '\t\t\t_stringVariables.Clear();\n';
  output += '\t\t\t_numberVariables.Clear();\n';
  output += '\t\t\t_percentageVariables.Clear();\n';
  output += '\t\t\t_booleanVariables.Clear();\n';
  output += '\t\t\t_rangeVariables.Clear();\n';
  output += '\t\t\t_isInitialized = false;\n';
  output += '\t\t\tInitialize();\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Resets a specific variable group to default values</summary>\n';
  output += '\t\t/// <param name="group">Variable group name to reset</param>\n';
  output += '\t\tpublic static void ResetGroup(string group)\n\t\t{\n';
  output += '\t\t\tGrimoireLog.Info($"Resetting variable group: {group}");\n\n';
  output += '\t\t\t// Remove all variables from the specified group\n';
  output += '\t\t\tList<string> keysToRemove = new List<string>();\n\n';
  output += '\t\t\tforeach (var key in _stringVariables.Keys)\n\t\t\t{\n';
  output += '\t\t\t\tif (key.StartsWith(group + ":"))\n\t\t\t\t\tkeysToRemove.Add(key);\n';
  output += '\t\t\t}\n';
  output += '\t\t\tforeach (var key in keysToRemove)\n\t\t\t\t_stringVariables.Remove(key);\n\n';
  output += '\t\t\tkeysToRemove.Clear();\n';
  output += '\t\t\tforeach (var key in _numberVariables.Keys)\n\t\t\t{\n';
  output += '\t\t\t\tif (key.StartsWith(group + ":"))\n\t\t\t\t\tkeysToRemove.Add(key);\n';
  output += '\t\t\t}\n';
  output += '\t\t\tforeach (var key in keysToRemove)\n\t\t\t\t_numberVariables.Remove(key);\n\n';
  output += '\t\t\tkeysToRemove.Clear();\n';
  output += '\t\t\tforeach (var key in _percentageVariables.Keys)\n\t\t\t{\n';
  output += '\t\t\t\tif (key.StartsWith(group + ":"))\n\t\t\t\t\tkeysToRemove.Add(key);\n';
  output += '\t\t\t}\n';
  output += '\t\t\tforeach (var key in keysToRemove)\n\t\t\t\t_percentageVariables.Remove(key);\n\n';
  output += '\t\t\tkeysToRemove.Clear();\n';
  output += '\t\t\tforeach (var key in _booleanVariables.Keys)\n\t\t\t{\n';
  output += '\t\t\t\tif (key.StartsWith(group + ":"))\n\t\t\t\t\tkeysToRemove.Add(key);\n';
  output += '\t\t\t}\n';
  output += '\t\t\tforeach (var key in keysToRemove)\n\t\t\t\t_booleanVariables.Remove(key);\n\n';
  output += '\t\t\tkeysToRemove.Clear();\n';
  output += '\t\t\tforeach (var key in _rangeVariables.Keys)\n\t\t\t{\n';
  output += '\t\t\t\tif (key.StartsWith(group + ":"))\n\t\t\t\t\tkeysToRemove.Add(key);\n';
  output += '\t\t\t}\n';
  output += '\t\t\tforeach (var key in keysToRemove)\n\t\t\t\t_rangeVariables.Remove(key);\n\n';
  
  // Re-initialize the group
  output += '\t\t\t// Re-initialize the group from Variables class\n';
  output += '\t\t\tswitch (group)\n\t\t\t{\n';
  
  variables.forEach((variable, varIndex) => {
    if (!variable.data || !Array.isArray(variable.data) || variable.data.length === 0) {
      return;
    }
    
    const variableName = sanitizeName(variable.name || `Variable${varIndex}`);
    const elementType = variable.element_type || 'string';
    
    output += `\t\t\t\tcase "${variableName}":\n`;
    
    variable.data.forEach((element: any) => {
      const elementName = sanitizeName(element.name || element.id || 'Unknown');
      const key = `${variableName}:${elementName}`;
      
      if (elementType === 'string') {
        output += `\t\t\t\t\t_stringVariables["${key}"] = Variables.${variableName}.${elementName}.Value;\n`;
      } else if (elementType === 'number') {
        output += `\t\t\t\t\t_numberVariables["${key}"] = Variables.${variableName}.${elementName}.GetValue();\n`;
      } else if (elementType === 'percentage') {
        output += `\t\t\t\t\t_percentageVariables["${key}"] = Variables.${variableName}.${elementName}.GetValue();\n`;
      } else if (elementType === 'boolean') {
        output += `\t\t\t\t\t_booleanVariables["${key}"] = Variables.${variableName}.${elementName}.GetValue();\n`;
      } else if (elementType === 'range') {
        output += `\t\t\t\t\t_rangeVariables["${key}"] = new RangeValue(Variables.${variableName}.${elementName}.GetMin(), Variables.${variableName}.${elementName}.GetMax(), Variables.${variableName}.${elementName}.GetIncrement());\n`;
      }
    });
    
    output += `\t\t\t\t\tbreak;\n`;
  });
  
  output += '\t\t\t\tdefault:\n';
  output += '\t\t\t\t\tGrimoireLog.Warn($"Unknown variable group: {group}");\n';
  output += '\t\t\t\t\tbreak;\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Resets a specific variable to its default value</summary>\n';
  output += '\t\t/// <param name="group">Variable group name</param>\n';
  output += '\t\t/// <param name="name">Variable element name</param>\n';
  output += '\t\tpublic static void ResetVariable(string group, string name)\n\t\t{\n';
  output += '\t\t\tstring key = MakeKey(group, name);\n';
  output += '\t\t\tGrimoireLog.Info($"Resetting variable: {group}.{name}");\n\n';
  output += '\t\t\t// This is a simplified reset - in a full implementation,\n';
  output += '\t\t\t// you would look up the specific variable from the Variables class\n';
  output += '\t\t\t// For now, we just remove it and let the caller reinitialize if needed\n';
  output += '\t\t\t_stringVariables.Remove(key);\n';
  output += '\t\t\t_numberVariables.Remove(key);\n';
  output += '\t\t\t_percentageVariables.Remove(key);\n';
  output += '\t\t\t_booleanVariables.Remove(key);\n';
  output += '\t\t\t_rangeVariables.Remove(key);\n';
  output += '\t\t}\n\n';
  
  output += '\t\t/// <summary>Gets the initialization status</summary>\n';
  output += '\t\t/// <returns>True if initialized</returns>\n';
  output += '\t\tpublic static bool IsInitialized()\n\t\t{\n';
  output += '\t\t\treturn _isInitialized;\n';
  output += '\t\t}\n\n';
  output += '\t\t#endregion\n';
  
  output += '\t}\n';
  output += '}\n';
  
  console.log('✅ Runtime variables file rendered successfully');
  
  return output;
}
